import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { apiService } from './services/api';
import AuthForm from './components/Auth/AuthForm';
import KGList from './components/KG/KGList';
import KGDetails from './components/KG/KGDetails';
import VersionDetails from './components/Version/VersionDetails';
import Layout from './components/Layout/Layout';
import './App.css';

// Inner component that can use useNavigate (must live inside <Router>)
function AppRoutes({ onLogout, freshLogin }: { onLogout: () => void; freshLogin: boolean }) {
  const navigate = useNavigate();

  // On fresh login (not page-refresh), redirect to /kg regardless of current URL
  useEffect(() => {
    if (freshLogin) navigate('/kg', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    onLogout();
  };

  return (
    <Layout onLogout={handleLogout}>
      <Routes>
        <Route path="/" element={<Navigate to="/kg" replace />} />
        <Route path="/kg" element={<KGList />} />
        <Route path="/kg/:kgId" element={<KGDetails />} />
        <Route path="/kg/:kgId/version/:versionId" element={<VersionDetails />} />
        <Route path="*" element={<Navigate to="/kg" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // true = restored from localStorage (page refresh); false = fresh login
  const [restoredSession, setRestoredSession] = useState(false);

  useEffect(() => {
    const pollyEnv = localStorage.getItem('pollyEnv');
    const apiKey = localStorage.getItem('apiKey');

    if (pollyEnv && apiKey) {
      try {
        apiService.initialize(pollyEnv as any, apiKey);
        setIsAuthenticated(true);
        setRestoredSession(true); // allow deep links on page refresh
      } catch (err) {
        console.error('Failed to initialize API:', err);
      }
    }
    setLoading(false);
  }, []);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    setRestoredSession(false); // fresh login → redirect to /kg
  };

  const handleLogout = () => {
    localStorage.removeItem('pollyEnv');
    localStorage.removeItem('apiKey');
    setIsAuthenticated(false);
  };

  if (loading) {
    return <div className="app-loading"><div className="app-loading-spinner" /></div>;
  }

  if (!isAuthenticated) {
    return <AuthForm onAuthenticated={handleAuthenticated} />;
  }

  return (
    <Router>
      <AppRoutes onLogout={handleLogout} freshLogin={!restoredSession} />
    </Router>
  );
}

export default App;
